import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DollarSign, TrendingUp, Users, Ticket, Star, ArrowUpRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformAdminBilling = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/platform-admin/billing`, { headers: getAuthHeaders() });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch billing data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Billing & Revenue</h1>
        <p className="text-muted-foreground">Financial overview and subscription management</p>
      </div>

      {/* MRR Overview */}
      <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 p-6 rounded-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl uppercase">Monthly Recurring Revenue</h2>
          <div className="flex items-center gap-1 text-green-400 text-sm">
            <ArrowUpRight size={14} />
            <span>+12.5% vs last month</span>
          </div>
        </div>
        <p className="font-heading text-5xl text-green-400 mb-6">
          ${data?.mrr?.total?.toLocaleString() || 0}
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-black/20 p-4 rounded-sm">
            <p className="text-sm text-muted-foreground">Coach Standard</p>
            <p className="text-xl font-heading">${data?.mrr?.coach_standard?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground">{data?.coach_plans?.standard || 0} subs × $19.99</p>
          </div>
          <div className="bg-black/20 p-4 rounded-sm">
            <p className="text-sm text-muted-foreground">Coach Pro</p>
            <p className="text-xl font-heading">${data?.mrr?.coach_pro?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground">{data?.coach_plans?.pro || 0} subs × $39.99</p>
          </div>
          <div className="bg-black/20 p-4 rounded-sm">
            <p className="text-sm text-muted-foreground">Athlete Pass</p>
            <p className="text-xl font-heading">${data?.mrr?.athlete_pass?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground">{data?.passes?.base || 0} passes × $9.99</p>
          </div>
          <div className="bg-black/20 p-4 rounded-sm">
            <p className="text-sm text-muted-foreground">Athlete Plus</p>
            <p className="text-xl font-heading text-primary">${data?.mrr?.athlete_plus?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground">{data?.passes?.plus || 0} passes × $14.99</p>
          </div>
        </div>
      </div>

      {/* Subscription Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Coach Plans */}
        <div className="bg-card border border-border/40 p-6 rounded-sm">
          <div className="flex items-center gap-2 mb-4">
            <Users className="text-blue-400" size={20} />
            <h2 className="font-heading text-lg uppercase">Coach Subscriptions</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
              <div>
                <p className="font-medium">Standard Plan</p>
                <p className="text-sm text-muted-foreground">$19.99/month</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-heading">{data?.coach_plans?.standard || 0}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
              <div>
                <p className="font-medium">Pro Plan</p>
                <p className="text-sm text-muted-foreground">$39.99/month</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-heading">{data?.coach_plans?.pro || 0}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
        </div>

        {/* Athlete Passes */}
        <div className="bg-card border border-border/40 p-6 rounded-sm">
          <div className="flex items-center gap-2 mb-4">
            <Ticket className="text-green-400" size={20} />
            <h2 className="font-heading text-lg uppercase">Athlete Passes</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
              <div>
                <p className="font-medium">Athlete Pass (Base)</p>
                <p className="text-sm text-muted-foreground">$9.99/month</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-heading">{data?.passes?.base || 0}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-primary/10 border border-primary/30 rounded-sm">
              <div className="flex items-center gap-2">
                <Star className="text-primary" size={16} />
                <div>
                  <p className="font-medium">Athlete Pass Plus</p>
                  <p className="text-sm text-muted-foreground">$14.99/month</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-heading text-primary">{data?.passes?.plus || 0}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Coaches */}
      <div className="bg-card border border-border/40 p-6 rounded-sm">
        <h2 className="font-heading text-lg uppercase mb-4">Coach Subscription Details</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-xs uppercase text-muted-foreground py-3 px-2">Coach</th>
                <th className="text-left text-xs uppercase text-muted-foreground py-3 px-2">Plan</th>
                <th className="text-left text-xs uppercase text-muted-foreground py-3 px-2">Status</th>
                <th className="text-left text-xs uppercase text-muted-foreground py-3 px-2">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(data?.coaches || []).slice(0, 10).map((coach, idx) => (
                <tr key={idx} className="border-b border-border/40">
                  <td className="py-3 px-2">
                    <p className="font-medium text-sm">{coach.full_name}</p>
                    <p className="text-xs text-muted-foreground">{coach.email}</p>
                  </td>
                  <td className="py-3 px-2">
                    <span className="text-sm capitalize">{coach.subscription?.plan || 'None'}</span>
                  </td>
                  <td className="py-3 px-2">
                    <span className={`px-2 py-1 text-xs rounded ${
                      coach.subscription?.status === 'active' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {coach.subscription?.status || 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-sm">
                    ${coach.subscription?.plan === 'pro' ? '39.99' : coach.subscription?.plan === 'standard' ? '19.99' : '0.00'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminBilling;
